import { useState, useRef } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { Formik } from 'formik';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import {
  AllowedMembersAlert,
  TagsInput,
  MemberMultiSelectDropdown,
} from '../common';
import { teamService } from '../../services';
import {
  INVITATIONS,
  UPDATE_FAILURE_ERROR,
  LOCAL_STORAGE_USER_DATA,
} from '../../constants';
import { fetchOnBoardProgress } from '../../actions/user';
import { getUserData } from '../../helpers';
import { sendAmplitudeData } from '../../utilities/amplitude.js';

const InviteToProjectModal = ({ members, projectId, invites }) => {
  const dispatch = useDispatch();
  const [show, setShow] = useState(false);
  const [isSubmitting, setSubmitting] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);
  const teamData = useSelector((state) => state.team);
  const sortedMembersList = teamData.members.filter(
    (member) =>
      member.status !== 'inactive' &&
      !members.some((b) => member.userId === b.userId) &&
      !invites.some((invitee) => member.email === invitee.email)
  );
  const existingTeamMembers = [];
  const newTeamMembers = [];
  const selectInputRef = useRef(null);
  const tagsInputRef = useRef(null);

  const handleSendInvites = (formdata) => {
    setSubmitting(true);
    const existingMembers = [...formdata.addExistingTeamMembers];
    const newMembers =
      [...formdata.addNewTeamMembers].length > 0
        ? [...formdata.addNewTeamMembers].map((newInvitee) => newInvitee.value)
        : [];
    const inviteesList = [...existingMembers, ...newMembers];
    const params = {
      teamOwnerId: localStorage.getItem('teamOwnerId'),
      projectId: JSON.stringify([projectId]),
      invites: JSON.stringify(inviteesList),
    };

    teamService
      .update_team_invitations(params)
      .then((response) => {
        if (response.status) {
          const userData = JSON.parse(
            localStorage.getItem(LOCAL_STORAGE_USER_DATA)
          );
          if (userData.subscriptionDetails.currentPlan === 'trial') {
            dispatch(fetchOnBoardProgress());
            sendAmplitudeData('Invited member to project', {
              page: 'Member invite project detail modal',
              status: 'Success',
            });
          }
          // Clear input values
          selectInputRef.current.select.clearValue();
          tagsInputRef.current.select.select.clearValue();

          if (
            response.data.SentInvites &&
            response.data.SentInvites.length > 0
          ) {
            toast.success(INVITATIONS.SEND_INVITATIONS);
          }
          // Show error message for existing invites
          if (
            response.data.ExistingInvites &&
            response.data.ExistingInvites.length > 0
          ) {
            response.data.ExistingInvites.map((eInvite) =>
              toast.error(eInvite)
            );
          }
          handleClose();
        }
        setSubmitting(false);
      })
      .catch((error) => {
        setSubmitting(false);
        if (
          error.data.ExistingInvites &&
          error.data.ExistingInvites.length > 0
        ) {
          error.data.ExistingInvites.map((eInvite) => toast.error(eInvite));
        } else {
          toast.error(UPDATE_FAILURE_ERROR);
        }
      });
  };
  const activeMembers = getUserData('activeMembers') || 0;
  const maxMembersAllowed = getUserData('maxMembersAllowed') || 0;
  return (
    <>
      <Button
        variant="light"
        size="lg"
        className="btn-rounded-circle btn-white lift"
        onClick={handleShow}
      >
        +
      </Button>

      <Modal show={show} onHide={handleClose} centered>
        <Modal.Header>
          <Modal.Title className="m-0">Invite team members</Modal.Title>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={handleClose}
          />
        </Modal.Header>
        <Formik
          enableReinitialize
          initialValues={{
            addExistingTeamMembers: existingTeamMembers,
            addNewTeamMembers: newTeamMembers,
          }}
          onSubmit={(values, { resetForm }) => {
            handleSendInvites(values);
            resetForm();
          }}
        >
          {(formikprops) => (
            <Form onSubmit={formikprops.handleSubmit}>
              <Modal.Body>
                <AllowedMembersAlert />
                <section>
                  <MemberMultiSelectDropdown
                    id="selectInputRef"
                    name="addExistingTeamMembers"
                    labelText="Add existing Team Member(s)"
                    fromArray={sortedMembersList}
                    formProps={formikprops}
                    isDisabled={sortedMembersList.length < 1 && true}
                    innerRef={selectInputRef}
                  />
                  {maxMembersAllowed - activeMembers > 0 && (
                    <TagsInput
                      id="tagInputRef"
                      name="addNewTeamMembers"
                      labelText="Invite new Team Member(s)"
                      placeHolderText="Type email address and press enter"
                      formProps={formikprops}
                      innerRef={tagsInputRef}
                    />
                  )}
                </section>
              </Modal.Body>
              <Modal.Footer className="d-flex justify-content-between">
                <Button variant="link" onClick={handleClose}>
                  Close
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={
                    !(formikprops.isValid && formikprops.dirty) || isSubmitting
                  }
                >
                  {isSubmitting && (
                    <span
                      className="spinner-border spinner-border-sm me-1"
                      role="status"
                      aria-hidden="true"
                    />
                  )}
                  Invite
                </Button>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal>
    </>
  );
};

InviteToProjectModal.propTypes = {
  members: PropTypes.array,
  invites: PropTypes.array,
  projectId: PropTypes.string,
};

export default InviteToProjectModal;
